import { StepContent } from "~/components/StepContent"
import { IdentityFields } from "~/components/settings/IdentityFields"
import { CopyableField } from "~/components/CopyableField"
import { LoadingState } from "~/components/screens/StateScreen"
import { FieldError } from "~/components/ui/field"
import { Spinner } from "~/components/ui/spinner"

import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Link } from "~/components/Link"
import { toast } from "sonner"
import { OrbitLogo } from "~/components/icons/logos"
import { Button } from "~/components/ui/button"

import { authClient } from "~/lib/auth-client"
import { fileToBase64 } from "~/lib/file-upload"
import { shareUrl } from "~/lib/native-share"

import type { RouterOutputs } from "~/trpc/react"
import { api } from "~/trpc/react"
import Steps, { STEPS } from "./steps"

function OnboardingContent({ firstOrg }: { firstOrg: boolean }) {
	const [currentStep, setCurrentStep] = useState<number>(STEPS.WELCOME)
	const [organization, setOrganization] =
		useState<RouterOutputs["org"]["create"]>()
	const [inviteUrl, setInviteUrl] = useState<string>()
	const [isLoadingInvite, setIsLoadingInvite] = useState(false)
	const [orgState, setOrgState] = useState<RouterOutputs["org"]["create"]>({
		message: "",
		name: undefined,
		slug: undefined
	})
	const [loading, setLoading] = useState(false)

	const createOrg = api.org.create.useMutation()
	const editOrg = api.org.edit.useMutation()

	const createInvite = api.org.createOrgInviteLink.useMutation({
		onSuccess: (data) => {
			setInviteUrl(data.url)
			setIsLoadingInvite(false)
		}
	})
	const uploadImage = api.files.uploadOrgIcon.useMutation()

	useEffect(() => {
		if (currentStep === STEPS.INVITE && organization?.org?.id && !inviteUrl) {
			setIsLoadingInvite(true)
			createInvite.mutate({
				organizationId: organization.org.id
			})
		}
	}, [currentStep, organization, inviteUrl, createInvite.mutate])

	const copyToClipboard = async () => {
		if (!inviteUrl) return
		await shareUrl({
			title: "Orbit invite",
			text: `Join ${organization?.org?.name ?? "my organization"} on Orbit.`,
			url: inviteUrl,
			dialogTitle: "Share invite"
		})
	}

	const renderStep = () => {
		switch (currentStep) {
			case STEPS.WELCOME:
				return (
					<main className="w-full">
						<StepContent spacing="compact">
							<p className="text-text-sub font-normal">
								Thanks for joining us, we're happy to have you. Get started by
								creating your{firstOrg ? " first" : ""} organization and team,
								and then invite your team members to join you.
							</p>
							<Button
								className="w-96"
								onClick={() => setCurrentStep(STEPS.ORGANIZATION)}
								size="xl"
							>
								Get Started
							</Button>
						</StepContent>
					</main>
				)

			case STEPS.ORGANIZATION:
				return (
					<main className="w-xl max-w-full">
						<StepContent spacing="spacious">
							<h1 className="text-2xl font-bold">Create Organization</h1>
							<form
								className="flex flex-col gap-4 w-full"
								onSubmit={async (e) => {
									e.preventDefault()
									setLoading(true)
									const form = e.currentTarget
									const formData = new FormData(form)

									const logoFile = formData.get("logo") as File | null
									try {
										if (
											logoFile &&
											logoFile.size > 0 &&
											(logoFile.size > 1024 * 1024 ||
												!["image/png", "image/jpeg"].includes(logoFile.type))
										) {
											throw new Error(
												"Logo must be a PNG or JPEG image under 1 MB"
											)
										}
										const created = await createOrg.mutateAsync({
											name: formData.get("name")?.toString() || "",
											slug: formData.get("slug")?.toString() || ""
										})
										if (!created.org) {
											setOrgState(created)
											return
										}
										setOrganization(created)
										if (logoFile && logoFile.size > 0) {
											try {
												const result = await uploadImage.mutateAsync({
													organizationId: created.org.id,
													name: logoFile.name,
													type:
														logoFile.type === "image/png"
															? "image/png"
															: "image/jpeg",
													data: await fileToBase64(logoFile)
												})
												await editOrg.mutateAsync({
													organizationId: created.org.id,
													name: created.org.name,
													slug: created.org.slug,
													logo: result.url
												})
											} catch {
												toast.error(
													"Organization created, but its icon could not be uploaded. You can retry in organization settings."
												)
											}
										}
										setCurrentStep(STEPS.INVITE)
									} catch (error) {
										toast.error(
											error instanceof Error
												? error.message
												: "Failed to create organization"
										)
									} finally {
										setLoading(false)
									}
								}}
							>
								<IdentityFields
									kind="Organization"
									disabled={loading}
									nameError={orgState.name}
									slugError={orgState.slug}
								/>

								<Button type="submit" disabled={loading} pending={loading}>
									Create Organization
								</Button>
							</form>
						</StepContent>
					</main>
				)

			case STEPS.INVITE:
				if (!organization?.org?.id) {
					setCurrentStep(STEPS.ORGANIZATION)
					return null
				}

				return (
					<main className="w-xl max-w-full">
						<StepContent spacing="default">
							<div className="flex flex-col gap-2">
								<h1 className="text-2xl font-bold">Invite Users</h1>
								<p className="text-sm text-muted-foreground">
									Share the invitation link with your team members to get
									started.
								</p>
								<p className="text-xxs text-muted-foreground">
									This invite link expires after 48 hours.
								</p>
							</div>
							{isLoadingInvite ? (
								<Spinner size="md" />
							) : !inviteUrl ? (
								<div className="flex flex-col gap-2 items-center">
									{createInvite.isError && (
										<FieldError>
											{createInvite.error.message ||
												"Failed to create invite link. Please try again."}
										</FieldError>
									)}
									<Button
										onClick={() => {
											if (!organization?.org?.id) return
											setIsLoadingInvite(true)
											createInvite.mutate({
												organizationId: organization?.org?.id
											})
										}}
										variant="outline"
										size="sm"
									>
										{createInvite.isError ? "Retry" : "Generate Invite Link"}
									</Button>
								</div>
							) : (
								<CopyableField
									value={inviteUrl}
									label="Invitation link"
									onCopy={copyToClipboard}
								/>
							)}
							<Button
								className="w-96"
								onClick={() => setCurrentStep(STEPS.COMPLETE)}
								size="xl"
							>
								Next
							</Button>
						</StepContent>
					</main>
				)

			case STEPS.COMPLETE:
				return (
					<main className="w-full">
						<StepContent spacing="compact">
							<p className="text-text-sub font-normal">
								You're all set! You can now start creating issues on Orbit!
							</p>
							<Link to={`/${organization?.org?.slug}`}>
								<Button className="w-96" size="xl">
									Blast Off!
								</Button>
							</Link>
						</StepContent>
					</main>
				)

			default:
				return null
		}
	}

	return (
		<div className="flex flex-col gap-4 items-center justify-center w-3xl text-center h-screen my-4">
			<header className="flex flex-col gap-2 items-center justify-center font-semibold">
				<OrbitLogo width={140} />
				{currentStep === STEPS.WELCOME && (
					<h1 className="text-text-main text-4xl">Welcome to Orbit</h1>
				)}
			</header>
			{renderStep()}
			<footer className="fixed bottom-8 w-full xl:px-48 px-32">
				<Steps currentStep={currentStep} />
			</footer>
		</div>
	)
}

export default function OnboardingPage() {
	const { data: session, isPending: isSessionPending } = authClient.useSession()
	const { data: orgs, isPending: isOrgPending } =
		authClient.useListOrganizations()
	const navigate = useNavigate()

	useEffect(() => {
		if (!session && !isSessionPending) {
			navigate("/login")
		}
	}, [session, isSessionPending, navigate])

	return (
		<div className="h-screen w-screen flex items-center justify-center bg-orbit-gradient">
			{isSessionPending || isOrgPending ? (
				<LoadingState scope="page" />
			) : (
				<OnboardingContent firstOrg={orgs?.length === 0} />
			)}
		</div>
	)
}
